import type * as Sentry from "@sentry/angular";
import { classifyError, ErrorClassification } from "./error-classification";
import { classifyReportedError } from "./uncaught-error-classification";
import { LoggedError, NamedError } from "./reported-errors";

/**
 * Set a grouping fingerprint (and a matching title) where Sentry's default
 * grouping by stack trace would split one problem into many issues.
 * Summarized in the logging README ("How events are grouped and filtered");
 * the rules are documented on the functions below.
 *
 * @param classification of the event's error chain (see `classifyErrorChain`)
 */
export function groupSentryEvent(
  event: Sentry.ErrorEvent,
  hint: Sentry.EventHint,
  classification?: ErrorClassification,
): Sentry.ErrorEvent {
  groupByCause(event, hint, classification);
  if (classification === "server-error") {
    groupByStatusAndEndpoint(event);
  }
  return event;
}

function groupByCause(
  event: Sentry.ErrorEvent,
  hint: Sentry.EventHint,
  classification?: ErrorClassification,
): Sentry.ErrorEvent {
  const values = event.exception?.values;
  if (!values?.length) {
    if (event.message) {
      event.fingerprint = [fingerprintKey(event.message)];
    }
    return event;
  }

  const thrown = values[values.length - 1];
  const original = hint?.originalException;
  const errors = errorsOfExceptions(values, original);
  const chainClassifications = errors
    ? errors.map((error) => classifyReportedError(error))
    : values.map(classifyException);
  const reportedError =
    original instanceof NamedError || original instanceof LoggedError
      ? thrown
      : rewrappedNamedError(values, original);

  if (reportedError) {
    // a connectivity failure itself (e.g. an unhandled `ConnectivityError`)
    if (
      chainClassifications[values.indexOf(reportedError)] === "connectivity"
    ) {
      return groupAsNetworkError(event, thrown, reportedError);
    }
    return groupByRootCause(
      event,
      values,
      chainClassifications,
      reportedError,
      rootCauseIndex(errors),
    );
  }

  if (classification === "connectivity") {
    return groupAsNetworkError(event, thrown);
  }

  if (!values.some((v) => v.stacktrace?.frames?.length)) {
    // without a stack Sentry groups by the raw message: one issue per id or url
    event.fingerprint = [thrown.type || "Error", fingerprintKey(thrown.value)];
    reportNormalized(event, thrown);
  }
  return event;
}

/**
 * The error objects of the event's exceptions (innermost first, like `values`),
 * if Sentry's exceptions are their `cause` chain; else (e.g. an event without
 * the original error) `undefined`, to judge by the serialized exceptions.
 */
function errorsOfExceptions(
  values: Sentry.Exception[],
  original: unknown,
): Error[] | undefined {
  const errors: Error[] = [];
  for (
    let error = original;
    error instanceof Error && errors.length < values.length;
    error = error.cause
  ) {
    errors.unshift(error);
  }
  return errors.length === values.length && !(errors[0]?.cause instanceof Error)
    ? errors
    : undefined;
}

/**
 * The index of the root cause: the innermost of our typed errors, as the foreign
 * error a boundary converted into it (its `cause`) only details it; else (also
 * without the error objects) the innermost exception.
 */
function rootCauseIndex(errors: Error[] | undefined): number {
  return Math.max(errors?.findIndex((error) => classifyError(error)) ?? 0, 0);
}

/** The classification of an exception as Sentry serialized it, without the error object. */
export function classifyException(
  exception: Sentry.Exception,
): ErrorClassification | undefined {
  return classifyReportedError({
    name: exception.type,
    message: exception.value ?? "",
  });
}

/** Whether nothing caught the reported error: it reached Angular's `ErrorHandler` or the window. */
export function isUnhandled(event: Sentry.ErrorEvent): boolean {
  return event.exception?.values?.at(-1)?.mechanism?.handled === false;
}

/**
 * One of our errors that a framework wrapped without adding anything but its
 * stack, e.g. Angular re-throwing an error raised in a `resource()` loader.
 */
function rewrappedNamedError(
  values: Sentry.Exception[],
  original: unknown,
): Sentry.Exception | undefined {
  const cause = (original as { cause?: unknown } | undefined)?.cause;
  if (!(cause instanceof NamedError) || values.length < 2) {
    return undefined;
  }
  // `toString()` of the cause gives "Error: <message>"
  const wrapperValue = fingerprintKey(values[values.length - 1].value).replace(
    /^error /,
    "",
  );
  const named = values[values.length - 2];
  return wrapperValue === fingerprintKey(named.value) ? named : undefined;
}

/**
 * Group one failure into one issue.
 *
 * A wrapped error chain is grouped by its root cause alone (with the component's
 * message as the `reported_by` tag) only if the root cause is a classified failure
 * that means the same whichever component ran into it: a broken local database,
 * a server fault, a missing permission. All other chains are grouped by the wrapper
 * and the root cause: a generic root cause (e.g. a `missing` document or a
 * `TypeError`) in another component is likely a different bug and gets an issue
 * (and new-issue alert) of its own.
 * A network failure is grouped by the wrapper as well, as the wrapper says what
 * could not be done (e.g. `AppBootstrapError` caused by a failed request).
 *
 * `values` are ordered innermost-first: `values[0]` is the root cause.
 */
function groupByRootCause(
  event: Sentry.ErrorEvent,
  values: Sentry.Exception[],
  chainClassifications: (ErrorClassification | undefined)[],
  reportedError: Sentry.Exception,
  rootIndex: number,
): Sentry.ErrorEvent {
  const reportedIndex = values.indexOf(reportedError);
  const reportedKey = [
    reportedError.type ?? "",
    groupingValue(reportedError, chainClassifications[reportedIndex]),
  ];
  const rootPosition = Math.min(rootIndex, reportedIndex);
  const rootCause = values[rootPosition];
  const rootClassification = chainClassifications[rootPosition];
  const rootKey = [
    rootCause.type ?? "",
    groupingValue(rootCause, rootClassification),
  ];
  // Sentry titles an event by its thrown error
  const thrown = values[values.length - 1];

  // a wrapper repeating its cause's type and message is one failure, not two
  if (rootCause === reportedError || rootKey.join() === reportedKey.join()) {
    event.fingerprint = reportedKey;
    thrown.type = reportedError.type;
    thrown.value = reportedError.value;
    return event;
  }

  if (!rootClassification || rootClassification === "connectivity") {
    const rootText =
      rootClassification === "connectivity"
        ? NETWORK_FAILURE
        : normalizeErrorValue(rootCause.value);
    event.fingerprint = [...reportedKey, ...rootKey];
    thrown.type = reportedError.type;
    thrown.value = `${reportedError.value} (caused by ${rootCause.type ?? "Error"}: ${rootText})`;
    return event;
  }

  event.fingerprint = rootKey;
  const reportedBy = `${reportedError.type ?? "Error"}: ${normalizeErrorValue(reportedError.value)}`;
  event.tags = { ...event.tags, reported_by: truncateTag(reportedBy) };
  event.extra = { ...event.extra, reported_by: reportedBy };
  thrown.type = rootCause.type;
  thrown.value = normalizeErrorValue(rootCause.value);
  return event;
}

/**
 * A server fault (whoever logged it) gets an issue per status and endpoint: grouped by the
 * root cause alone, a new failing endpoint would otherwise not open an issue
 * of its own. The endpoint is the masked `requestPath` the boundaries add.
 */
function groupByStatusAndEndpoint(event: Sentry.ErrorEvent) {
  const status = event.extra?.status;
  if (typeof status !== "number") {
    return;
  }
  const requestPath = event.extra?.requestPath;
  const key = [String(status)];
  if (typeof requestPath === "string") {
    key.push(requestPath);
    event.tags = { ...event.tags, request_path: truncateTag(requestPath) };
  }
  event.tags = { ...event.tags, http_status: truncateTag(String(status)) };
  if (event.fingerprint?.length) {
    event.fingerprint = [...event.fingerprint, ...key];
  }
  const thrown = event.exception?.values?.at(-1);
  if (thrown) {
    thrown.value = `${thrown.value} (HTTP ${key.join(" ")})`;
  }
}

/**
 * Collect every "could not reach the server" into one issue, at level warning:
 * it usually is one device's connection, and an outage shows as this issue rising.
 * The browser-specific wording is replaced by a stable title and kept as a tag.
 */
function groupAsNetworkError(
  event: Sentry.ErrorEvent,
  thrown: Sentry.Exception,
  failedRequest: Sentry.Exception = thrown,
): Sentry.ErrorEvent {
  event.fingerprint = [NETWORK_ERROR_FINGERPRINT];
  event.level = "warning";
  event.tags = {
    ...event.tags,
    network_error: truncateTag(
      `${failedRequest.type ?? "Error"}: ${normalizeErrorValue(failedRequest.value)}`,
    ),
  };
  event.extra = { ...event.extra, originalError: failedRequest.value };

  thrown.type = "NetworkError";
  thrown.value = "Failed to reach the server";
  return event;
}

/** The fingerprint of the one issue collecting all connectivity failures. */
export const NETWORK_ERROR_FINGERPRINT = "network-error";

/** Stands in for a connectivity failure, which every browser words differently. */
const NETWORK_FAILURE = "network failure";

function groupingValue(
  exception: Sentry.Exception,
  classification: ErrorClassification | undefined,
): string {
  return classification === "connectivity"
    ? NETWORK_FAILURE
    : fingerprintKey(exception.value);
}

/**
 * Report an exception under its normalized message, which is its grouping key
 * here, keeping the original as extra data (an id per row, or a quoted HTML
 * error page, would otherwise be the issue title).
 */
function reportNormalized(
  event: Sentry.ErrorEvent,
  exception: Sentry.Exception,
) {
  if (!exception.value) {
    return;
  }
  const normalized = normalizeErrorValue(exception.value).slice(0, 300);
  if (normalized !== exception.value) {
    event.extra = { ...event.extra, originalError: exception.value };
    exception.value = normalized;
  }
}

/** Sentry rejects tag values longer than 200 characters. */
export function truncateTag(value: string): string {
  return value.slice(0, 200);
}

/**
 * Values that vary between occurrences of one problem, masked before a message
 * is used as a grouping key. The more specific patterns have to run first.
 */
const VOLATILE_VALUE_PATTERNS: [RegExp, string][] = [
  // a response body quoted into the message, e.g. an error page of the proxy
  [/ with body ["'][\s\S]*$/i, ""],
  [/https?:\/\/\S+/gi, "<url>"],
  [
    /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi,
    "<uuid>",
  ],
  [/\b\d+-[0-9a-f]{32}\b/gi, "<rev>"],
  // legacy entity ids embedding a username (`User:some.person`); not "TypeError: ..."
  [/\b[A-Z][A-Za-z]{2,}:[A-Za-z0-9._<>-]+/g, "<entityId>"],
  [/\d+/g, "<n>"],
];

/** Mask ids, urls and numbers, staying human-readable for titles. */
export function normalizeErrorValue(value: string | undefined): string {
  if (!value) {
    return "<none>";
  }
  return VOLATILE_VALUE_PATTERNS.reduce(
    (normalized, [pattern, placeholder]) =>
      normalized.replace(pattern, placeholder),
    value,
  );
}

/**
 * Reduce a message to a grouping key: normalized, and without the casing and
 * punctuation that libraries are inconsistent about ("Unauthorized"/"unauthorized").
 */
export function fingerprintKey(value: string | undefined): string {
  return normalizeErrorValue(value)
    .toLowerCase()
    .replace(/[.,;:!?]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}
